"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { BookOpen, Layers, ShieldCheck, Microscope, FileText, ArrowRight, CircleCheck as CheckCircle2, CircleQuestionMark as HelpCircle, Sparkles, Download, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const keyPillars = [{
  id: 1,
  title: "Pharmacopeial Compliance",
  description: "Methodologies aligned with USP <616>, USP <1174>, and Ph. Eur. 2.9.36 for tapped density, bulk density, and flowability.",
  iconName: "ShieldCheck",
  tag: "Regulatory Standards"
}, {
  id: 2,
  title: "Flow Characterisation",
  description: "In-depth insights into Carr's Compressibility Index, Hausner Ratio, and angle of repose for robust formulation development.",
  iconName: "Layers",
  tag: "Material Science"
}, {
  id: 3,
  title: "Process Analytical Integration",
  description: "Bridging benchtop powder testing with real-time in-line NIR and Raman process monitoring across solid dosage pipelines.",
  iconName: "Microscope",
  tag: "PAT & Inline"
}, {
  id: 4,
  title: "Method Development & Training",
  description: "Standard operating procedures, qualification protocols (IQ/OQ), and hands-on analyst education for lab consistency.",
  iconName: "FileText",
  tag: "Lifecycle Services"
}];
const knowledgeTopics = [{
  id: 1,
  title: "Tapped vs. Bulk Density Assessment",
  category: "Technical Guide",
  readTime: "6 min read",
  linkUrl: "/PowderTestingPage"
}, {
  id: 2,
  title: "Overcoming Segregation in High-Speed Tableting",
  category: "Application Note",
  readTime: "8 min read",
  linkUrl: "/TabletTestingPage"
}, {
  id: 3,
  title: "Selecting the Right Funnel & Orifice for Shear Cell Tests",
  category: "Best Practice",
  readTime: "5 min read",
  linkUrl: "/PowderTabletTestingInstrumentsPage"
}];
export default function PowderKnowledge() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="aboutpowdertestingknowledgecentre" data-nav="light" className="bg-background py-20 lg:py-28 text-foreground">
      <div className="page-container space-y-12">
        {/* Breadcrumb Navigation */}
        <nav aria-label="breadcrumb">
          <ol className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground break-words sm:gap-2">
            <li className="inline-flex items-center gap-1.5">
              <Link to="/HomePage" className="transition-colors hover:text-foreground">
                Home
              </Link>
            </li>
            <li aria-hidden="true" className="text-muted-foreground/60">
              <ChevronRight className="size-3.5" />
            </li>
            <li className="inline-flex items-center gap-1.5">
              <Link to="/ResourcesPage" className="transition-colors hover:text-foreground">
                Resources
              </Link>
            </li>
            <li aria-hidden="true" className="text-muted-foreground/60">
              <ChevronRight className="size-3.5" />
            </li>
            <li className="inline-flex items-center gap-1.5">
              <span aria-current="page" className="font-medium text-foreground">
                Powder Testing Knowledge Centre
              </span>
            </li>
          </ol>
        </nav>

        {/* Header Block */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8 space-y-4">
            <div className="inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs uppercase tracking-wider font-semibold">
                <Sparkles className="size-3.5 text-primary" />
                Knowledge Centre
              </Badge>
              <span className="text-xs text-muted-foreground uppercase tracking-widest font-mono">
                Technical Expertise Hub
              </span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-[1.15]">
              Authoritative Guidance for Powder & Bulk Solids Behaviour
            </h2>
            
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl font-sans leading-relaxed">
              Our Powder Testing Knowledge Centre brings together decades of qualification experience, pharmacopeial standardisation, and hands-on laboratory troubleshooting across Ireland and the UK.
            </p>
          </div>

          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-end lg:items-end">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/ApplicationConsultationPage">
                Discuss Your Application
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/FeasibilityStudiesPage">
                Request Feasibility Study
              </Link>
            </Button>
          </div>
        </div>

        <Separator />

        {/* Feature Grid & Visual Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
          {/* Main Visual Card */}
          <motion.div initial={{
          opacity: 0,
          scale: shouldReduceMotion ? 1 : 0.98
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col justify-between rounded-2xl bg-secondary/50 border border-border p-6 sm:p-8 overflow-hidden relative">
            <div className="space-y-6 relative z-10">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono font-medium text-muted-foreground uppercase tracking-wider">
                  Operational Core
                </span>
                <Badge variant="outline" className="bg-background/80">
                  Ph. Eur. / USP
                </Badge>
              </div>

              <div className="space-y-3">
                <h3 className="text-2xl font-default font-bold text-foreground">
                  Empowering Regulated Labs with Reproducible Data
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Powder properties directly impact tableting speed, blending uniformity, and batch consistency. We help formulation scientists and QC managers eliminate guesswork through standardised instrumentation.
                </p>
              </div>

              <div className="rounded-xl overflow-hidden border border-border bg-card shadow-sm">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_lab.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_lab.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_lab.png-wvc-sizes" alt="Laboratory analyst performing pharmaceutical powder flow testing" loading="lazy" decoding="async" className="w-full h-48 object-cover" />
              </div>

              <ul className="space-y-2.5 pt-2">
                <li className="flex items-start gap-2.5 text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Comprehensive instrument calibration & validation services</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>Direct support from dedicated applications engineers</span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <span>On-site operator training and SOP documentation</span>
                </li>
              </ul>
            </div>

            <div className="pt-6 mt-6 border-t border-border flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Need instrument specifics?
              </span>
              <Button asChild variant="link" className="p-0 h-auto font-medium">
                <Link to="/PowderTabletTestingInstrumentsPage">
                  Browse Instruments
                  <ArrowRight className="size-3.5 ml-1" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Pillars & Topics Section */}
          <div className="lg:col-span-7 flex flex-col justify-between gap-8">
            {/* Core Pillars 2x2 */}
            <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
            once: true
          }} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {keyPillars.map((pillar, i) => <motion.div key={pillar.id} data-index={i} variants={itemVariants}>
                  <Card className="h-full border border-border bg-card hover:border-primary/50 transition-[border-color,box-shadow] duration-200 shadow-xs">
                    <CardContent className="p-5 flex flex-col justify-between h-full space-y-4">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="p-2 rounded-lg bg-secondary text-primary">
                            {pillar.iconName === "ShieldCheck" && <ShieldCheck className="size-5" />}
                            {pillar.iconName === "Layers" && <Layers className="size-5" />}
                            {pillar.iconName === "Microscope" && <Microscope className="size-5" />}
                            {pillar.iconName === "FileText" && <FileText className="size-5" />}
                          </div>
                          <Badge variant="outline" className="text-[10px] text-muted-foreground">
                            {pillar.tag}
                          </Badge>
                        </div>

                        <h4 className="text-base font-default font-semibold text-foreground">
                          {pillar.title}
                        </h4>

                        <p className="text-xs text-muted-foreground leading-relaxed">
                          {pillar.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>)}
            </motion.div>

            {/* Featured Guides list */}
            <div className="rounded-xl border border-border bg-card p-6 shadow-xs space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BookOpen className="size-4 text-primary" />
                  <h4 className="text-sm font-default font-semibold text-foreground">
                    Featured Knowledge Articles & Technical Guides
                  </h4>
                </div>
                <Button asChild variant="ghost" size="sm" className="text-xs h-7">
                  <Link to="/ResourcesPage">
                    View All Resources
                  </Link>
                </Button>
              </div>

              <div className="divide-y divide-border">
                {knowledgeTopics.map((topic, i) => <div key={topic.id} data-index={i} className="py-3.5 first:pt-1 last:pb-1 flex items-center justify-between gap-4 group">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-medium text-primary uppercase tracking-wider">
                          {topic.category}
                        </span>
                        <span className="text-muted-foreground text-xs">&bull;</span>
                        <span className="text-[11px] text-muted-foreground">
                          {topic.readTime}
                        </span>
                      </div>
                      <Link to={topic.linkUrl} className="text-sm font-medium text-foreground hover:text-primary transition-colors block">
                        {topic.title}
                      </Link>
                    </div>

                    <Button asChild variant="secondary" size="icon" className="size-8 rounded-full shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Link to={topic.linkUrl}>
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </Button>
                  </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA Banner */}
        <div className="rounded-2xl bg-secondary/80 border border-border p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center md:text-left">
            <h4 className="text-lg font-default font-semibold text-foreground">
              Have specific testing standards or powder characteristics to evaluate?
            </h4>
            <p className="text-sm text-muted-foreground">
              Schedule a technical session with our laboratory specialist team in Ireland & the UK.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <Button asChild variant="default" size="default">
              <Link to="/BookADemoPage">
                Book a Live Demo
              </Link>
            </Button>
            <Button asChild variant="outline" size="default">
              <Link to="/ContactPage">
                Contact Technical Support
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}